import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Dimmer, Segment, Loader, Dropdown } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { TableWrapper, Header, NotFoundWrapper, ListWrapper } from './styles';
import { iRootDispatch, iRootState } from '../../store';
import * as usersService from '../../services/user';
import IconButton from '../../components/IconButton';
import FilterBar, { FilterField, FilterValues } from '../../components/Filter';
import { translateUserRole, translateUserStatus } from '../../util';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { UserState } from '../../types/UserState';
import AuthorizationContainer from '../../components/AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { AvailableBrokersAndEstatesState } from '../../store/types/temp-types';
import { CustomDropDownOptions } from '../../types';
import { UserStatusEnum } from '../../enums/user-status.enum';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';
import TablePaginationMenu from '../../components/TablePaginationMenu';
import { getEstateUnits } from '../../services/estate-unit';
import { EstateUnitStatusEnum } from '../../types/EstateUnit';
import { resolveUserEstateUnitName } from '../../utils/userEstateUnit.util';

type OptionsType = {
  value: string;
  text: string;
  key: number;
};

const statusOptions: CustomDropDownOptions[] = [
  {
    key: 1,
    text: 'Ativo',
    value: UserStatusEnum.active
  },
  {
    key: 2,
    text: 'Inativo',
    value: UserStatusEnum.inactive
  },
  {
    key: 3,
    text: 'Pendente',
    value: UserStatusEnum.pending
  }
];

const FILTER_INITIAL_VALUES: FilterValues = {
  type: 'name',
  argument: '',
  estate: '',
  role: '',
  status: '',
  estateUnitId: ''
};

const normalizeFilters = (values: FilterValues): FilterValues => {
  const normalized: FilterValues = {};

  if (values.type && values.argument) {
    normalized[values.type] = values.argument;
  }

  if (values.estate) normalized.estate = values.estate;
  if (values.role) normalized.role = values.role;
  if (values.status) normalized.status = values.status;
  if (values.estateUnitId) normalized.estateUnitId = values.estateUnitId;

  return normalized;
};

const UsersList = (props: Props) => {
  const user: UserState = props.user;
  const availableBrokersAndEstates = props.availableBrokersAndEstates;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstLoading, setIsFirstLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UserState[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [dataCount, setDataCount] = useState<number>(0);
  const [filters, setFilters] = useState<FilterValues>({ ...FILTER_INITIAL_VALUES });
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [estateUnitOptions, setEstateUnitOptions] = useState<OptionsType[]>([]);

  const userRole = user.role as UserRolesEnum;
  const isBackOffice = isBackOfficeUser();
  const confiaxUser = user.broker !== null;
  const roleOptions = availableBrokersAndEstates.roles;

  const getUsers = useCallback(
    async (overrideFilters?: FilterValues | null) => {
      try {
        setIsLoading(true);

        const searchId = user.broker
          ? String(user.broker.id)
          : user.estate
            ? String(user.estate.id)
            : '';

        const estateId = user?.estate ? String(user?.estate?.id) : '';
        const source =
          overrideFilters === null
            ? {}
            : normalizeFilters(overrideFilters !== undefined ? overrideFilters : filters);

        const requestFilters = userRole.includes('BROKER')
          ? source
          : {
              ...source,
              estate: estateId || source.estate
            };

        const usersList = await usersService.getUsers(
          searchId,
          currentPage,
          requestFilters
        );

        setUsers(usersList.data);
        if (currentPage === 1) setDataCount(usersList.count);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [user.broker, user.estate, userRole, currentPage, filters]
  );

  useEffect(() => {
    if (!isFirstLoading) getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, user.broker, user.estate, userRole]);

  useEffect(() => {
    if (!isBackOffice) getUsers();
    setIsFirstLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (options.length === 0) {
      const estates = availableBrokersAndEstates.estates.map(estate => ({
        key: estate.key,
        text: estate.text,
        value: estate.value.id
      }));
      setOptions(estates);
    }
  }, [availableBrokersAndEstates.estates, options.length]);

  useEffect(() => {
    const estateId = filters.estate || (user.estate?.id ? String(user.estate.id) : '');

    if (!estateId || !isBackOffice) {
      setEstateUnitOptions([]);
      return;
    }

    getEstateUnits({ estateId, status: EstateUnitStatusEnum.active })
      .then(units => {
        setEstateUnitOptions(
          (units || []).map((unit, index) => ({
            key: index,
            text: unit.name,
            value: unit.id
          }))
        );
      })
      .catch(() => setEstateUnitOptions([]));
  }, [filters.estate, isBackOffice, user.estate?.id]);

  const filterFields: FilterField[] = useMemo(() => {
    const toSelectOptions = (items: Array<{ value: any; text: string }>) =>
      items.map(item => ({
        value: String(item.value),
        label: String(item.text)
      }));

    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'name',
        types: [
          { value: 'name', label: 'Nome' },
          { value: 'email', label: 'E-mail' }
        ],
        width: 1.6
      }
    ];

    if (isBackOffice) {
      fields.push({
        kind: 'select',
        name: 'estate',
        label: 'Imobiliária',
        placeholder: 'Selecione',
        options: toSelectOptions(options),
        loading: options.length === 0
      });

      if (estateUnitOptions.length > 0) {
        fields.push({
          kind: 'select',
          name: 'estateUnitId',
          label: 'Unidade',
          placeholder: 'Selecione',
          options: [
            { value: 'null', label: 'Sem unidade' },
            ...toSelectOptions(estateUnitOptions)
          ],
          getDisplayValue: values => {
            if (!values.estateUnitId) return undefined;
            if (values.estateUnitId === 'null') return 'Sem unidade';
            const option = estateUnitOptions.find(
              item => String(item.value) === String(values.estateUnitId)
            );
            return option ? option.text : String(values.estateUnitId);
          }
        });
      }
    }

    if (confiaxUser) {
      fields.push({
        kind: 'select',
        name: 'role',
        label: 'Função',
        placeholder: 'Selecione',
        options: toSelectOptions(roleOptions || []),
        loading: !roleOptions || roleOptions.length === 0,
        getDisplayValue: values => {
          const option = (roleOptions || []).find(
            item => String(item.value) === String(values.role)
          );
          return option ? String(option.text) : undefined;
        }
      });

      fields.push({
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecione',
        options: toSelectOptions(statusOptions),
        getDisplayValue: values => {
          const option = statusOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? String(option.text) : undefined;
        }
      });
    }

    return fields;
  }, [isBackOffice, confiaxUser, options, estateUnitOptions, roleOptions]);

  const handleSearch = (values: FilterValues) => {
    const next = { ...values };

    // Trocar imobiliária zera a unidade
    if (next.estate !== filters.estate) {
      next.estateUnitId = '';
    }

    setIsFirstLoading(false);
    setFilters(next);

    if (currentPage === 1) {
      getUsers(next);
    } else {
      setCurrentPage(1);
    }
  };

  const handleClear = () => {
    setIsFirstLoading(false);
    // clearAll do FilterBar já chama onSearch com os valores zerados
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getCompanyName = (userItem: UserState) => {
    if (userItem.broker) return userItem.broker.name;
    if (userItem.estate) return userItem.estate.name;
    if (userItem.franchise) return userItem.franchise.name;
  };

  function renderTableData(usersList: UserState[]) {
    return usersList.map(userItem => (
      <Table.Row key={userItem.id} textAlign={'left'}>
        <Table.Cell width={3}>{userItem.name}</Table.Cell>
        <Table.Cell width={2}>{userItem.email}</Table.Cell>
        <Table.Cell width={2}>{translateUserStatus(userItem.status)}</Table.Cell>
        <Table.Cell width={2}>{translateUserRole(userItem.role)}</Table.Cell>
        <Table.Cell width={2}>{getCompanyName(userItem)}</Table.Cell>
        <Table.Cell width={2}>{resolveUserEstateUnitName(userItem) || '-'}</Table.Cell>
        <Table.Cell width={1} textAlign={'right'}>
          <AuthorizationContainer action={ActionEnum.update} feature={FeatureEnum.users}>
            <Dropdown icon={'ellipsis vertical'} direction={'left'}>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() =>
                    (props as any).history.push('/admin/users-edit/', {
                      id: userItem.id
                    })
                  }
                >
                  Editar Usuário
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </AuthorizationContainer>
        </Table.Cell>
      </Table.Row>
    ));
  }

  return (
    <>
      <ListWrapper>
        <FilterBar
          fields={filterFields}
          initialValues={FILTER_INITIAL_VALUES}
          onSearch={handleSearch}
          onClear={handleClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="users"
        />
        <Dimmer.Dimmable as={Segment} dimmed={isLoading}>
          <Dimmer active={isLoading} inverted>
            <Loader />
          </Dimmer>
          {users && users.length > 0 ? (
            <TableWrapper>
              <Table>
                <Header>
                  <Table.Row textAlign={'left'}>
                    <Table.HeaderCell width={3}>NOME</Table.HeaderCell>
                    <Table.HeaderCell width={2}>EMAIL</Table.HeaderCell>
                    <Table.HeaderCell width={2}>STATUS</Table.HeaderCell>
                    <Table.HeaderCell width={2}>FUNÇÃO</Table.HeaderCell>
                    <Table.HeaderCell width={2}>EMPRESA</Table.HeaderCell>
                    <Table.HeaderCell width={2}>UNIDADE</Table.HeaderCell>
                    <Table.HeaderCell width={1} textAlign={'right'}>
                      <IconButton name="setting" />
                    </Table.HeaderCell>
                  </Table.Row>
                </Header>
                <Table.Body>{renderTableData(users)}</Table.Body>
                <Table.Footer>
                  <Table.Row>
                    <Table.HeaderCell colSpan="17">
                      <TablePaginationMenu
                        currentPage={currentPage}
                        dataCount={dataCount}
                        setCurrentPage={setCurrentPage}
                      />
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Footer>
              </Table>
            </TableWrapper>
          ) : (
            <NotFoundWrapper>
              <p>Nenhum usuário cadastrado</p>
            </NotFoundWrapper>
          )}
        </Dimmer.Dimmable>
      </ListWrapper>
    </>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});
type Props = ReturnType<typeof mapState>;
const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapState, mapDispatch)(UsersList);
